import React from "react";
import { useNavigate, useLocation } from "react-router-dom";

interface SidebarItem {
  path: string;
  label: string;
  icon: string;
  badge?: number;
}

interface SlideBarGraphsProps {
  isOpen: boolean;
  onToggle: () => void;
}

const SlideBarGraphs: React.FC<SlideBarGraphsProps> = ({
  isOpen,
  onToggle,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = React.useState(false);
  const [navbarHeight, setNavbarHeight] = React.useState(56);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    const detectNavbarHeight = () => {
      const navbar = document.querySelector(".navbar");
      if (navbar) {
        setNavbarHeight(navbar.clientHeight);
      }
    };

    checkMobile();
    detectNavbarHeight();

    window.addEventListener("resize", () => {
      checkMobile();
      detectNavbarHeight();
    });

    setTimeout(detectNavbarHeight, 100);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const menuItems: SidebarItem[] = [
    {
      path: "/graficas#general",
      label: "Dashboard General",
      icon: "fa-chart-pie",
    },
    {
      path: "/graficas#documentos",
      label: "Gráficas de Documentos",
      icon: "fa-chart-line",
    },
    {
      path: "/graficas#chilitos",
      label: "Estadísticas Chilitos",
      icon: "fa-users",
    },
    {
      path: "/graficas#contratos",
      label: "Contratos Firmados",
      icon: "fa-file-signature",
    },
    {
      path: "/graficas#organigrama",
      label: "Organigrama",
      icon: "fa-sitemap",
    },
  ];

  const isActive = (path: string) => {
    if (path.includes("#")) {
      const [pathname, hash] = path.split("#");
      return location.pathname === pathname && location.hash === `#${hash}`;
    }

    return (
      location.pathname === path || location.pathname.startsWith(path + "/")
    );
  };

  return (
    <>
      {/* Overlay para móvil */}
      {isOpen && isMobile && (
        <div
          style={{
            ...styles.overlay,
            position: "fixed",
            top: `${navbarHeight}px`,
            left: "280px",
            right: 0,
            bottom: 0,
            zIndex: 997,
          }}
          onClick={onToggle}
          onTouchStart={onToggle}
        />
      )}

      {/* Sidebar - Desktop: columna del layout, Móvil: fixed overlay */}
      <div
        style={{
          ...styles.sidebar,
          ...(isMobile
            ? {
                position: "fixed",
                top: `${navbarHeight}px`,
                left: 0,
                height: `calc(100vh - ${navbarHeight}px)`,
                transform: isOpen ? "translateX(0)" : "translateX(-100%)",
                zIndex: 998,
              }
            : {
                position: "relative",
                display: isOpen ? "flex" : "none",
                minHeight: "100vh",
                height: "auto",
                transform: "none",
              }),
        }}
      >
        {/* Menú de navegación */}
        <nav style={styles.nav}>
          {menuItems.map((item, index) => {
            const active = isActive(item.path);
            return (
              <button
                key={index}
                onClick={() => {
                  navigate(item.path);

                  if (isMobile) {
                    onToggle();
                  }
                }}
                style={{
                  ...styles.navItem,
                  ...(active ? styles.navItemActive : {}),
                }}
                onMouseEnter={(e) => {
                  if (!active) {
                    e.currentTarget.style.background = "#f9fafb";
                    e.currentTarget.style.transform = "translateX(4px)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!active) {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.transform = "translateX(0)";
                  }
                }}
              >
                <div style={styles.navItemContent}>
                  <i
                    className={`fa-solid ${item.icon}`}
                    style={{
                      ...styles.navIcon,
                      color: active ? "rgb(47, 53, 59)" : "#6b7280",
                      background: active
                        ? "rgba(47, 53, 59, 0.1)"
                        : "transparent",
                    }}
                  ></i>
                  <span
                    style={{
                      ...styles.navLabel,
                      color: active ? "rgb(47, 53, 59)" : "#374151",
                      fontWeight: active ? 400 : 300,
                    }}
                  >
                    {item.label}
                  </span>
                  {item.badge && item.badge > 0 && (
                    <span style={styles.badge}>{item.badge}</span>
                  )}
                </div>
                {active && <div style={styles.activeIndicator} />}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Botón flotante para abrir sidebar (solo visible cuando está cerrado en móvil) */}
      {!isOpen && isMobile && (
        <button
          onClick={onToggle}
          style={{
            ...styles.floatingButton,
            position: "fixed",
            top: `${navbarHeight + 20}px`,
            left: "20px",
            zIndex: 999,
          }}
          aria-label="Abrir menú"
        >
          <i className="fa-solid fa-bars" style={styles.floatingButtonIcon}></i>
        </button>
      )}
    </>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0, 0, 0, 0.5)",
    zIndex: 997,
    display: "block",
  },
  sidebar: {
    position: "relative",
    width: "280px",
    minWidth: "280px",
    maxWidth: "280px",
    background: "rgba(255, 255, 255, 0.95)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    display: "flex",
    flexDirection: "column",
    overflowY: "auto",
    overflowX: "hidden",
    flexShrink: 0,
    margin: "16px",
    marginRight: "12px",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
  },
  sidebarHeader: {
    padding: "20px",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    minHeight: "80px",
  },
  sidebarHeaderContent: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flex: 1,
  },
  sidebarLogo: {
    fontSize: "24px",
    color: "#ffffff",
    width: "32px",
    height: "32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  sidebarTitle: {
    margin: 0,
    fontSize: "20px",
    fontWeight: 700,
    color: "#ffffff",
    whiteSpace: "nowrap",
  },
  toggleButton: {
    background: "rgba(255, 255, 255, 0.2)",
    border: "none",
    borderRadius: "8px",
    width: "36px",
    height: "36px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "background 0.2s ease",
    color: "#ffffff",
  },
  toggleIcon: {
    fontSize: "16px",
  },
  nav: {
    flex: 1,
    padding: "12px 8px",
    paddingTop: "24px",
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    overflowY: "auto",
  },
  navItem: {
    position: "relative",
    background: "transparent",
    border: "none",
    borderRadius: "8px",
    padding: "12px 16px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    transition: "all 0.2s ease",
    textAlign: "left",
    width: "100%",
    marginBottom: "4px",
    borderLeft: "3px solid transparent",
  },
  navItemActive: {
    background:
      "linear-gradient(90deg, rgba(47, 53, 59, 0.1) 0%, rgba(47, 53, 59, 0.05) 100%)",
    borderLeft: "3px solid rgb(47, 53, 59)",
  },
  navItemContent: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    width: "100%",
    position: "relative",
  },
  navIcon: {
    fontSize: "18px",
    width: "24px",
    height: "24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    borderRadius: "6px",
    transition: "all 0.2s ease",
  },
  navLabel: {
    fontSize: "14px",
    fontWeight: 300,
    flex: 1,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    letterSpacing: "0.2px",
  },
  badge: {
    background: "#ef4444",
    color: "#ffffff",
    borderRadius: "12px",
    padding: "2px 8px",
    fontSize: "11px",
    fontWeight: 600,
    minWidth: "20px",
    textAlign: "center",
  },
  activeIndicator: {
    position: "absolute",
    left: 0,
    top: "50%",
    transform: "translateY(-50%)",
    width: "3px",
    height: "70%",
    background: "rgb(47, 53, 59)",
    borderRadius: "0 3px 3px 0",
    boxShadow: "0 0 8px rgba(47, 53, 59, 0.4)",
  },
  sidebarFooter: {
    padding: "20px",
    borderTop: "1px solid #e5e7eb",
    background: "#f9fafb",
  },
  footerContent: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  footerIcon: {
    fontSize: "20px",
    color: "#667eea",
    width: "32px",
    height: "32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#ffffff",
    borderRadius: "8px",
    flexShrink: 0,
  },
  footerText: {
    flex: 1,
  },
  footerTitle: {
    margin: 0,
    fontSize: "13px",
    fontWeight: 600,
    color: "#374151",
    marginBottom: "2px",
  },
  footerSubtitle: {
    margin: 0,
    fontSize: "11px",
    color: "#6b7280",
  },
  floatingButton: {
    position: "fixed",
    left: "20px",
    width: "48px",
    height: "48px",
    borderRadius: "12px",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    border: "none",
    boxShadow: "0 4px 12px rgba(102, 126, 234, 0.4)",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 999,
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
  },
  floatingButtonIcon: {
    fontSize: "20px",
    color: "#ffffff",
  },
};

export default SlideBarGraphs;
